import { Component, OnInit } from '@angular/core';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { LazyLoadEvent } from 'primeng/api';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { FeatureEnum } from '@admin/shared/models/feature-enum';
import { UserService } from '../../../core/user.service';
import { LookupService } from '../../../shared/services/lookup.service';

import { PaymentListFilterModel } from '../shared/models/payment-list-filter-model';
import { PaymentListItemModel } from '../shared/models/payment-list-item-model';
import { PaymentService } from '../shared/services/payment.service';
import { ActivatedRoute } from '@angular/router';
import {
  PaymentChannelEnum,
  PaymentModel,
  AccountModel,
  InvoiceCoverageModel,
} from '@admin/billing/payments/shared/models/payment-model';
import { PaymentListTotalsModel } from '@admin/billing/payments/shared/models/payment-list-totals';
import { CustomerModel } from '../../../customers/shared/models/customer-model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-payment-list',
  templateUrl: './payment-list.component.html',
  styleUrls: ['./payment-list.component.less'],
  standalone: false,
})
export class PaymentListComponent implements OnInit {
  public filterPagingWrapper: PagingFilter<PaymentListFilterModel>;

  public payments: PaymentListItemModel[];
  public totalAmounts: PaymentListTotalsModel;

  public totalRecords: number;
  public pageSize: number;
  public dateFormat: string;
  public specialDateFormat: string;
  public hasPaymentsManagementPermission: boolean;

  public editingPayment: PaymentModel;
  public paymentAddMode: { isOn: boolean; edit: boolean };

  constructor(
    private lookupService: LookupService,
    private config: ConfigurationService,
    private translateService: TranslateService,
    private userService: UserService,
    private paymentService: PaymentService,
    private route: ActivatedRoute
  ) {
    this.filterPagingWrapper = new PagingFilter(new PaymentListFilterModel());
    this.payments = [];
    this.totalAmounts = null;
    this.editingPayment = null;
    this.paymentAddMode = { isOn: false, edit: false };
    this.hasPaymentsManagementPermission = false;
  }

  onLoad(event: LazyLoadEvent) {
    if (event.sortField && event.sortOrder) {
      this.filterPagingWrapper.sortParameters = [
        {
          column: event.sortField,
          order: event.sortOrder == 1 ? 'asc' : 'desc',
        },
      ];
    }
    this.filterPagingWrapper.pagingInfo.start = event.first;
    this.filterPagingWrapper.pagingInfo.limit = event.rows;
    this.onFilter();
  }

  onFilter() {
    this.filterPagingWrapper.pagingInfo.start =
      this.filterPagingWrapper.pagingInfo.start || 0;
    this.paymentService
      .listPayments(this.filterPagingWrapper)
      .subscribe((p) => {
        this.totalRecords = p.totalCount;
        this.payments = p.items;
      });

    this.paymentService
      .getPaymentsTotalAmounts(this.filterPagingWrapper)
      .subscribe((t) => {
        this.totalAmounts = t;
      });
  }

  onClearFilter() {
    this.filterPagingWrapper = new PagingFilter(new PaymentListFilterModel());
  }

  onApplyFilter() {
    this.filterPagingWrapper.pagingInfo.start = 0;

    this.onFilter();
  }

  onAddNewPayment() {
    this.editingPayment = new PaymentModel();
    this.editingPayment.amount = 0;
    this.editingPayment.amountLeft = 0;
    this.paymentAddMode.isOn = true;
    this.paymentAddMode.edit = false;
  }

  onFinishAddPayment() {
    this.paymentAddMode.isOn = false;
    this.refresh();
  }

  refresh() {
    this.onFilter();
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  onPaymentDelete(payment: PaymentListItemModel) {
    if (payment.paymentChannel != PaymentChannelEnum.BankTransfer) {
      alert(
        this.translateService.instant(
          'message.OnlyBankTransferPaymentManualDeleteAllowed'
        )
      );
      return;
    }

    var ans = confirm(this.translateService.instant('message.AreYouSure'));
    if (!ans) {
      return;
    }

    this.paymentService.deletePayment(payment.paymentId).subscribe((d) => {
      this.refresh();
    });
  }

  onEditPaymentClick(payment: PaymentListItemModel) {
    this.editingPayment = new PaymentModel();
    this.editingPayment.paymentId = payment.paymentId;
    this.editingPayment.amount = payment.amount;
    this.editingPayment.amountLeft = payment.amountLeft;
    this.editingPayment.comment = payment.comment;
    this.editingPayment.paymentDate = new Date(payment.paymentDate);
    this.editingPayment.payerAccount = new AccountModel();
    this.editingPayment.payerAccount.customer = new CustomerModel();
    this.editingPayment.payerAccount.customer.idNumber = payment.payerTaxId;
    this.editingPayment.activeInvoiceCoverageCodes = payment.invoiceCodes;

    this.paymentAddMode.isOn = true;
    this.paymentAddMode.edit = true;
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();
    this.pageSize = config.defaultPageSize;
    this.dateFormat = config.dateFormat;
    this.specialDateFormat = this.getSpecialDateFormat(this.dateFormat);
    this.hasPaymentsManagementPermission =
      this.userService.currentUser.features.some(
        (f) => f == FeatureEnum.ManagePayments
      );

    this.route.params.subscribe((p) => {
      let customerCredential = p['customer'];
      if (customerCredential) {
        this.filterPagingWrapper.filter.customerCredential = customerCredential;
      }
      let paymentCode = p['paymentCode'];
      if (paymentCode) {
        this.filterPagingWrapper.filter.paymentCode = paymentCode;
      }
    });
  }
}
